import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  Box,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  Select as ContextDropdown,
  Button,
  TextField,
  Alert,
} from "@mui/material";
import { AutoFixHigh as AiIcon } from "@mui/icons-material";
import styles from '../../styles/component.module.scss';
import aiStyles from '../../styles/aigenrator/index.module.scss'; 
// Utilities
import {
  addGeneratedTestCase,
  clearGeneratedTestCase,
} from "../../features/testCases/testCasesSlice";
import { getPrompts, generateTestCases } from "../../utils/api/ai_apis";
import { STATUS } from "../../utils/api/sharedfunction";
import { getLevel1Modules, getChildModules } from "../../utils/api/module_apis";
import { setActivePage } from "../../features/navigation/navigationSlice";
import { useSnackbar } from "../../utils/useSnackbar";
import theme from "../../theme/theme";
import DEFAULT_ROUTES from "../../constants/routes";
import { TestTypes } from "../../enums/TestType";
import RoleEnum from "../../enums/RoleEnum";
import { AI_FEATURE_KEYS } from "../../enums/AIModelEnums";
import { userCanSelectAiModel } from "../../utils/commonfunctions";

// components
import Select from "../Select/Select";
import WordCountTextarea from "../WordLimitedTextArea/WordLimitedTextArea";
import GeneratedTestCases from "../GeneratedTestCases/GeneratedTestCases";
import ApprovedTestCases from "../ApprovedTestCases/ApprovedTestCases";
import PageHeader from "../Header/PageHeader";
import ConfirmationModal from "../Modal/ConfirmationModal";
import AIModelRuntimeSelector from "../Select/llmModelSelector/AIModelRuntimeSelector";
import TabView from "../tabView/TabView";
import GhostLoader from "../Loaders/ghostloader";
import { CheckboxComponent, InputBox, Selectbox } from "../FormComponent";
import ButtonComponent from "../customButton";

const TestScenarioGenerator = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [input, setInput] = useState("");
  const [prompts, setPrompts] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [isAboveLength, setIsAboveLength] = useState(false);
  const [isBelowLength, setIsBelowLength] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const [requiredCaseCount, setRequiredCaseCount] = useState(5);
  const [selectedRequiredCaseCount, setSelectedRequiredCaseCount] =
    useState(40);

  const [requiredCaseType, setRequiredCaseType] = useState("Functional");
  const [selectedRequiredCaseTypes, setSelectedRequiredCaseTypes] = useState([
    TestTypes.BASIC,
    TestTypes.BIZARRE,
    TestTypes.EDGE_CASES,
    TestTypes.HIGH_PROBABILITY_BUGS,
  ]);

  const [isIncludeTestdata, setIsIncludeTestdata] = useState(false);
  const [isIncludeProjectContext, setIsIncludeProjectContext] = useState(false);

  const dispatch = useDispatch();
  const { generatedTestCases, approvedTestCases } = useSelector(
    (state) => state.testCases
  );
  const selectedProject = useSelector(
    (state) => state.projects.selectedProject
  );
  const activePage = useSelector((state) => state.navigation.activePage);
  const authUser = useSelector((state) => state.auth.user);
  const [selectedAiRuntimeModel, setSelectedAiRuntimeModel] = useState(null);
  const [aiModelSelectorKey, setAiModelSelectorKey] = useState(0);
  const canSelectAiModel = userCanSelectAiModel(
    authUser,
    selectedProject?.id ?? selectedProject?.project_id
  );

  const [projectContext, setProjectContext] = useState("");
  const [moduleContext, setModuleContext] = useState("");
  const [selectedModule, setSelelectedModule] = useState("");
  const [moduleContextList, setModuleContextList] = useState([]);

  const [componentContext, setComponentContext] = useState("");
  const [selectedComponent, setSelectedComponent] = useState("");
  const [componentContextList, setComponentContextList] = useState([]);
  const [contextTextareaValue, setContextTextareaValue] = useState("");
  const [contextChekbox, setContextCheckbox] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);

  const [context, setContext] = useState("");
  const [touched, setTouched] = useState(false);
  const [areErrors, setAreErrors] = useState({
    scenario: false,
    type: false,
  });

  const minWordLimit = 25;
  const maxWordLimit = 150;

  const handleModuleDropdownChange = async (event) => {
    setSelelectedModule(event.target.value);
    const selectedModuleObj = moduleContextList.find(
      (module) => module.id === event.target.value
    );
    setModuleContext(selectedModuleObj?.context || ""); // Fallback if context is null

    try {
      const response = await getChildModules({ module_id: event.target.value });
      if (response.status === STATUS.SUCCESS) {
        setComponentContextList(response.data || []);
      } else {
        console.error("Error fetching child modules: ", response.error);
        setComponentContextList([]);
      }
    } catch (error) {
      console.error("Error fetching child modules: ", error);
      setComponentContextList([]);
    }
  };

  const handleComponentChange = async (event) => {
    setSelectedComponent(event.target.value);
    const selectedComponentObj = componentContextList.find(
      (component) => component.id === event.target.value
    );
    setComponentContext(selectedComponentObj?.context || "");
  };

  useEffect(() => {
    const getDropdownOptions = async () => {
      try {
        const response = await getLevel1Modules();
        if (response.status === STATUS.SUCCESS) {
          setModuleContextList(response.data || []);
        } else {
          console.error("Error fetching modules: ", response.error);
        }
      } catch (error) {
        console.error("Error fetching modules: ", error);
      }
    };
    getDropdownOptions();
  }, []);

  // Ensure this is not undefined

  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const response = await getPrompts();
        if (response.status === STATUS.SUCCESS) {
          setPrompts(response.data || []);
        } else {
          console.error("Error fetching prompts:", response.error);
          showSnackbar(response.error || "Failed to fetch prompts", "error");
        }
      } catch (error) {
        console.error("Error fetching prompts:", error);
        showSnackbar("Failed to fetch prompts", "error");
      }
    };

    fetchPrompts();
  }, []);

  // useEffect(() => {
  //   if (prompts.length > 0) {
  //     console.log("setting prompts: ",prompts);
  //     console.log("value: ",prompts[0].value);
  //     setSelectedPrompt(prompts[0].displayTitle);
  //   }
  // }, [prompts]);

  useEffect(() => {
    if (prompts.length > 0) {
      setSelectedPrompt(prompts[0].prompt); // Set default value to the first prompt's id
    }
  }, [prompts]);

  const isWordCountValid = !isAboveLength && !isBelowLength;

  const handlePromptChange = (e) => {
    setSelectedPrompt(e.target.value);
  };

  const handleTypeChange = (e) => {
    setSelectedRequiredCaseTypes(e.target.value);
  };

  const handleCountChange = (e) => {
    setSelectedRequiredCaseCount(e.target.value);
  };

  const resetProjectContext = () => {
    setSelelectedModule(""); // Reset module dropdown
    setSelectedComponent(""); // Reset component dropdown
    setContextTextareaValue(""); // Clear textarea
    setContext("");
    setProjectContext("");
    setModuleContext("");
    setComponentContext("");
  };

  const handleReset = () => {
    setInput(""); // Clear textarea
    setContextCheckbox(false); // Reset checkbox for context
    setSelectedRequiredCaseCount(40); // Reset count dropdown
    setSelectedPrompt(prompts[0].prompt); // Reset prompt dropdown
    setSelectedRequiredCaseTypes([
      TestTypes.FUNCTIONAL,
      TestTypes.POSITIVE,
      TestTypes.NEGATIVE,
      TestTypes.PERFORMANCE,
      TestTypes.SECURITY,
      TestTypes.UI_SPECIFIC,
      TestTypes.BACKEND,
    ]);

    // Reset checkbox
    setIsIncludeProjectContext(false);
    setAiModelSelectorKey((k) => k + 1);

    // Reset project context
    resetProjectContext();

    showSnackbar("Fields reset successfully", "success");
  };

  useEffect(() => {
    const combinedContext = `${projectContext} ${moduleContext} ${componentContext}`;
    setContextTextareaValue(combinedContext.trim());
    console.log("combinedContext: ", combinedContext);
    setContext(combinedContext); // Call your setContext function here
  }, [projectContext, moduleContext, componentContext]);

  const handleContextChange = (e) => {
    setContextTextareaValue(e.target.value);
    setContext(e.target.value); // Call the setContext function on manual input
  };

  const handleGenerate = async () => {
    // check if fields are valid
    if (checkError()) {
      console.log("Error in fields", areErrors);
      console.log("total , ", checkError());
      return;
    }
    if (!selectedAiRuntimeModel?.provider || !selectedAiRuntimeModel?.model_key) {
      showSnackbar(
        "No AI model configured for AI Generator. Configure a model in Administration > Configuration.",
        "error"
      );
      return;
    }

    setLoading(true);
    const userInput = "Here is my application context: " + context + input;
    const modelConfig =
      selectedAiRuntimeModel?.provider &&
      selectedAiRuntimeModel?.model_key &&
      selectedAiRuntimeModel?.ai_api_key_id
        ? {
            provider: selectedAiRuntimeModel.provider,
            model: selectedAiRuntimeModel.model_key,
            id: selectedAiRuntimeModel.ai_api_key_id,
          }
        : undefined;
    try {
      dispatch(clearGeneratedTestCase());
      const response = await generateTestCases({
        input: userInput,
        additionalPrompt: selectedPrompt,
        types: selectedRequiredCaseTypes,
        count: selectedRequiredCaseCount,
        isIncludeTestdata: isIncludeTestdata,
        modelConfig,
      });

      if (response.status === STATUS.SUCCESS) {
        // Handle the new response structure
        const testCases = response.data?.testCases || response.data || [];
        if (Array.isArray(testCases)) {
          testCases.forEach((testCase) =>
            dispatch(addGeneratedTestCase(testCase))
          );
        }
        setActiveTab(0);
        showSnackbar(response.message || "Test Scenarios generated successfully", "success");
      } else {
        showSnackbar(response.error || "Error generating test scenarios", "error");
      }
    } catch (error) {
      console.error("Error generating test cases:", error);
      showSnackbar("Error generating test scenarios", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (event) => {
    setIsIncludeTestdata(event.target.checked);
  };


  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const promptOptions = prompts.map((prompt) => ({
    value: prompt.prompt,
    label: prompt.displayTitle,
  }));

  function handleBreadcrumbClick(id) {
    let {rURL, activePage=null} = (() => {
      if (id == "home") {
        return {
          rURL: DEFAULT_ROUTES.BASE_URL,
          activePage: "Projects",
        };
      }
      if (id == "project") {
        return {
          rURL: `${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${selectedProject.id}/test-cases`,
          activePage: "Test Cases",
        };
      }
    })();
    if (activePage) {
      dispatch(setActivePage(activePage));
    }
    navigate(rURL);
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: "Project",
      onClick: () => handleBreadcrumbClick("home"),
    },
    {
      label: selectedProject?.name || "Unknown Project",
      onClick: () => handleBreadcrumbClick("project"),
    },
    { label: activePage },
  ];

  const promptCaseTypeOptions = Object.values(TestTypes).map((type) => ({
    value: type,
    label: type,
  }));

  const promptCaseCount = [5, 10, 15, 20, 30, 40];

  const promptCaseCountOptions = promptCaseCount.map((count) => ({
    value: count,
    label: count,
  }));

  const checkError = () => {
    let hasError = false;

    // Check if scenario is empty
    if (!isWordCountValid) {
      setAreErrors((prev) => ({ ...prev, scenario: true }));
      hasError = true;
    } else {
      setAreErrors((prev) => ({ ...prev, scenario: false }));
    }

    return hasError;
  };

  const onResetClick = (value) => {
    setContextCheckbox(value);
    if (!value) {
      // Reset project context
      resetProjectContext();
    }
  };

  const isButtonDisabled = () => {
    const isInvalidWordCount = !isWordCountValid;
    const isLoading = loading;
    const noCaseTypesSelected = selectedRequiredCaseTypes.length === 0;

    return (
      isInvalidWordCount || isLoading || noCaseTypesSelected
    );
  };

  const tabsData = [
    {
      name: "Generated Scenarios",
      component: <GeneratedTestCases />,
    },
    {
      name: "Approved Scenarios",
      component: <ApprovedTestCases />,
    },
  ];

  return (
    <>
      <Box className={styles['page-container']}>
        <Box>
          <PageHeader
            projectName={selectedProject?.name}
            title="AI Test Scenario Generator"
            description="Generate, review, and approve AI-assisted test scenarios for your project."
            activePage={activePage}
            breadcrumbItems={breadcrumbItems}
            actions={[]}
          />
        </Box>
        <Box className={`${styles['cover-container-col']} scroll-y`}>
          <Box className={aiStyles['container']}>
            {/* Header Section */}
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5}}>
              <p className={aiStyles['title']}>
                AI Assisted Test Scenario Generator
              </p>
              <AiIcon className="icon" />
            </Box>

            {/* Body Section */}
            <Box>
              {/* Textarea Section */}
              <Box sx={{ mt: 2, width: "100%", mb: 1.5, position: "relative" }}>
                <WordCountTextarea
                  value={input}
                  onChange={setInput}
                  wordLimit={maxWordLimit}
                  minWords={minWordLimit}
                  setIsAboveLength={setIsAboveLength}
                  setIsBelowLength={setIsBelowLength}
                  disabled={loading}
                  onFocus={() => setTouched(true)}
                  onBlur={() => setTouched(false)}
                  error={touched && !isWordCountValid}
                />
              </Box>

              {/* Controls Section */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" }, // Stack on mobile, row on desktop
                  gap: 2,
                  width: "100%",
                  mb: 2,
                }}
              >
                <Selectbox 
                  label="Select max count"
                  value={selectedRequiredCaseCount}
                  options={promptCaseCountOptions}
                  onChange={handleCountChange}
                  disabled={loading}
                  className={aiStyles['selectCount']}
                />

                <AIModelRuntimeSelector
                  key={aiModelSelectorKey}
                  featureKey={AI_FEATURE_KEYS.AI_GENERATOR}
                  trialOrgSkip={authUser?.plan_name === "trial"}
                  required
                  disabled={loading}
                  readOnly={!canSelectAiModel}
                  onModelChange={setSelectedAiRuntimeModel}
                />
              </Box>

              {/* Context Section */}
              <Box sx={{ width: "100%" }}>
                <CheckboxComponent 
                  label="Include project context"
                  checked={contextChekbox}
                  onChange={(e) => onResetClick(e.target.checked)}
                  disabled={loading}
                  size="small"
                />

                {contextChekbox && (
                  <Box
                    sx={{
                      width: "100%",
                      mt: 2,
                    }}
                  >
                    {/* Module and Component Selection */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" }, // Stack on mobile, row on desktop
                        gap: 1,
                        width: "100%",
                      }}
                    >
                      <Selectbox 
                        id="module-dropdown"
                        label="Select Module"
                        value={selectedModule}
                        onChange={handleModuleDropdownChange}
                        options={moduleContextList.map((module) => ({
                          value: module.id,
                          label: module.name,
                        }))}
                        disabled={loading}
                      />
                      
                      <Selectbox 
                        label="Select Component"
                        value={selectedComponent}
                        onChange={handleComponentChange}
                        options={
                          componentContextList.length > 0
                            ? componentContextList.map((component) => ({
                                value: component.id,
                                label: component.name,
                              }))
                            : []
                        }
                        disabled={loading || !selectedModule}
                      />
                    </Box>
                    <Box sx={{mt:1}}>
                      <InputBox 
                        id="context-textarea"
                        label="Context Description"
                        type="text"
                        value={contextTextareaValue}
                        onChange={handleContextChange}
                        placeholder="Enter application, module, component context"
                        disabled={loading}
                      />
                    </Box>
                  </Box>
                )}
              </Box>
              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  mt: 2,
                  width: "100%",
                }}
              >
                <ButtonComponent 
                  onClick={() => setConfirmReset(true)}
                  disabled={loading}
                  variant="outline"
                  ctaText="Reset"
                  className="primary"
                />

                <ButtonComponent 
                  onClick={handleGenerate}
                  disabled={isButtonDisabled()}
                  ctaText="Generate"
                  className="bg-primary white"
                />
              </Box>
            </Box>
          </Box>
          <Box/>

          {/* Tabs Outside Card */}

          {((generatedTestCases && generatedTestCases.length > 0) ||
            (approvedTestCases && approvedTestCases.length > 0)) && (
              <Box sx={{ width: "100%", mt: 2 }}>
              <TabView tabsData={tabsData} />
            </Box>
          )}
        </Box>
      </Box>
      {loading && <GhostLoader />}
      <ConfirmationModal
        open={confirmReset}
        onClose={() => setConfirmReset(false)}
        onConfirm={() => {
          handleReset();
          setConfirmReset(false);
        }}
        title="Are you sure you want to reset?"
        message="This action cannot be undone. Please confirm if you want to proceed."
      />
    </>
  );
};

export default TestScenarioGenerator;
